import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastsManager } from 'ng2-toastr/ng2-toastr';
import { RefundsService } from '../../../services/refund.service';
import { decode } from 'jsonwebtoken';
import { Refund, RefundApprover } from '../../../models/Refund.model';
import { CommonHelper } from '../../../shared/helpers/CommonHelper';
import { ErrorHandlerProvider } from '../../../shared/providers/errorHandler.provider';
import { isBlockPeriodInvalid } from '../../../shared/helpers/validate-block-period';
import { AccountingPeriodBlockService } from '../../../services/accountingBlock.service';
import { AccountingPeriodBlock } from '../../../models/AccountingPeriodBlock.model';
import { AccountingType } from '../../../shared/enums/AccountingBlockType';
import { LocalStorage } from '../../../shared/wrappers/LocalStorage';
import { UserService } from "../../../services/user.service";
import { User } from "../../../models/User.model";
import { UserSearchModel } from "../../../models/UserSearch.model";
import * as _ from 'lodash';
import { DropDownList } from "../../../models/DropDownList.model";
import { JSZipProvider } from '../../../shared/providers/jszip.provider';
import { GUARDS } from "../../../shared/auth/Roles";
import { FileManagerService } from '../../../services/file-manager.service'

@Component({
  selector: 'app-refund-detail',
  templateUrl: './refund-detail.component.html',
  styleUrls: ['./refund-detail.component.scss']
})
export class RefundDetailComponent implements OnInit {

  loading;
  _id: string;
  status: number = 1;
  refund: Refund = new Refund();
  associatedBillingLetterAux: Boolean = false;
  sapDocNum: String = '';
  accountingBlock = new AccountingPeriodBlock();
  monthList;

  dropDownUsers: DropDownList[] = new Array<DropDownList>();
  selectedUsers: DropDownList[] = new Array<DropDownList>();
  dropDownUsersSettings = {};

  checkUpdateStatus: boolean = false;
  refundApproved: boolean = true;
  showSaveButton: boolean = false;
  users = new Array<User>();
  transcriptedUsersType = new Array<User>();
  user;
  GUARDS = GUARDS;
  descriptionsDict = {};
  oldApprovers: string[] = [];

  constructor(private refundsService: RefundsService,
              private router: Router,
              private route: ActivatedRoute,
              private commonHelper: CommonHelper,
              private errorHandler: ErrorHandlerProvider,
              private toastr: ToastsManager,
              private accountingBlockService: AccountingPeriodBlockService,
              private localStorage: LocalStorage,
              private userService: UserService,
              private jSZipProvider: JSZipProvider,
              private fileManager: FileManagerService
              ) { }

  async ngOnInit() {
    this.loading = true;
    this.user = _.cloneDeep(this.localStorage.retrieve("user"));
    this.accountingBlock = (await this.accountingBlockService.getAllBlockedPeriods())
        .find((ac: AccountingPeriodBlock) => ac.type === AccountingType.Refund);
    this.monthList = this.commonHelper.listAllMonths();
    this._id = this.route.snapshot.params['id'];
    this.refund = await this.refundsService.getRefund(this._id);

    for (let expense of this.refund.expenses) {
        if (expense.description) {
            this.descriptionsDict[expense._id] = {
                active: false,
                excerpt:  this.cropExpenseDescription(expense.description),
                fullDescription: expense.description
            };
        }
    }

    if(this.refund.associatedBillingLetter) this.associatedBillingLetterAux = true;
    this.oldApprovers = this.refund.approvers.map(approver => approver._id);
    this.checkMatchBetweenRefundAndApprover();
    await this.buildUsers();

    this.loading = false;
  }

  async buildUsers() {
    const userSearch: UserSearchModel = new UserSearchModel();
    userSearch.active = true;
    delete userSearch.limit;
    const usersResponse = (await this.userService.searchUsers(userSearch)).data;
    this.users = _.cloneDeep(usersResponse);
    const optionalApprovers = this.refund.approvers.filter((approver: RefundApprover) => approver.optional)
        .map((optionalUsers) => optionalUsers._id);
    this.transcriptedUsersType = usersResponse.map((user: User) => ({
        ...user,
        type: this.commonHelper.transcriptInternalRole(user.type),
        loadUserDataForGrid: () => _
    })).filter((user: User) => !optionalApprovers.includes(user._id));

    const changeOfApproversBlocked = this.refund.statusFinancial != 1;
    this.dropDownUsers = this.commonHelper.getDropDownListItems(this.transcriptedUsersType, '_id', 'name', ["type"])
    this.dropDownUsersSettings = this.commonHelper.getDropDownSettings(false, "Selecione os aprovadores", false, "Marcar todos os aprovadores", "Desmarcar todos os aprovadores", true, "type", "", changeOfApproversBlocked, 5, "Buscar");
    this.selectApprovers(optionalApprovers);
  }

  selectApprovers(optionalApprovers: string[]): void {
    this.selectedUsers = this.refund.approvers.map((approver: RefundApprover) => {
        if (!optionalApprovers.includes(approver._id)) {
            const selectApprover = this.users.find((user: User) => user._id === approver._id);
            return selectApprover ? {
                id: selectApprover._id,
                itemName: selectApprover.name,
                type: this.commonHelper.transcriptInternalRole(selectApprover.type)
            } : null;
        }
    }).filter(Boolean);
  }

  checkMatchBetweenRefundAndApprover() {
    const user = this.localStorage.retrieve('user');
    const decoded = decode(user.token);
    const loggedUserId = decoded.user._id;

    const approverOfRefund = this.refund.approvers.find(a => a._id === loggedUserId);
    if(approverOfRefund && !approverOfRefund.approved && this.refund.statusFinancial == 1)
      this.showSaveButton = true;
  }

  OnCancel(){
    this.router.navigate(['/refunds']);
  }

  valuePaidInvalid(): boolean {
    const customerPaid = this.refund.paidCustomer;
    const refundValue = this.refund.valuePaidCustomer;
    const totalRefundValue = this.refund.value;

    if (!customerPaid) {
      return false;
    }

    if (
      refundValue === undefined ||
      isNaN(refundValue) ||
      refundValue === 0 ||
      refundValue > totalRefundValue
    ) {
      return true;
    }
  }

  async saveRefund() {
    try{
       this.loading = true;
       if(this.showSaveButton && this.checkUpdateStatus){
        if(!this.refundApproved) {
            this.refund.statusFinancial = 4;
        } else {
            const refundDate = new Date(this.refund.created);
            const refundMonth = refundDate.getMonth() + 1;
            const refundYear = refundDate.getFullYear();
            const isInvalid = isBlockPeriodInvalid({
              competence: refundMonth,
              competenceYear: refundYear
            }, this.accountingBlock);
            if (isInvalid) {
              this.loading = false;
              return this.toastr.warning(`A competência
              ${this.monthList[refundDate.getMonth()].name.toLowerCase()}/${refundYear} deste reembolso
                  está dentro do intervalo de bloqueio. Não será possível editar`, 'Bloqueio Contábil', {
              toastLife:8000,
              showCloseButton: true
              });
            };

            let res = await this.refundsService.approval(this.refund);
            if(res._id){
              this.loading = false;
              this.toastr.success('Reembolso atualizado com sucesso');
              this.router.navigate(['/refunds']);
              return;
            } else {
              this.errorHandler.interceptError(res, 'Reembolso');
              this.router.navigate(['/refunds']);
              return;
            }
        }
      }

     const added = this.refund.approvers.filter(approver => !this.oldApprovers.includes(approver._id));
     const removed = this.oldApprovers.filter(approver => !this.refund.approvers.map(a => a._id).includes(approver));
     if (added.length > 0 || removed.length > 0) {
        let res = await this.refundsService.update(this.refund);
        if(res._id){
            this.loading = false;
            this.toastr.success('Reembolso atualizado com sucesso');
            return this.router.navigate(['/refunds']);
        }
        this.errorHandler.interceptError(res, 'Reembolso');
        return this.router.navigate(['/refunds']);
      }

      let res = await this.refundsService.update(this.refund);
        if(res._id){
          this.toastr.success('Reembolso atualizado com sucesso!');
        } else {
          this.errorHandler.interceptError(res, 'Reembolso');
        }
        this.loading = false;
        this.router.navigate(['/refunds']);
    }catch(err){
      this.loading = false;
      this.router.navigate(['/refunds']);
      this.errorHandler.interceptError(err, 'Reembolso');
    }
  }

  async getSapDocNum() {
    try {
        this.loading = true;
        let respSAP = await this.refundsService.getSapDocNumByRefund(this.refund.sapDocEntry);
        if(respSAP && respSAP.DocNum)
           this.sapDocNum = respSAP.DocNum;
        else
            this.errorHandler.interceptError(respSAP, 'Reembolso');
        this.loading = false;
    } catch(err) {
        this.loading = false;
        this.errorHandler.interceptError(err, 'Reembolso');
    }
  }

  onItemSelect(user: DropDownList): void {
    if (user.id && user.itemName) {
        this.refund.approvers.push({
            _id: user.id,
            name: user.itemName,
            approved: false,
            optional: false
        })
    }
  }

  validateUser(user: DropDownList): void {
    const deselectedUser = this.refund.approvers.find((approver) => approver._id === user.id);
    if (deselectedUser && !deselectedUser.optional && deselectedUser.approved) {
        this.toastr.warning("Você não pode remover aprovadores que já aprovaram este reembolso", "Aviso");
        this.selectedUsers.push({
            id: deselectedUser._id,
            itemName: deselectedUser.name,
            group: this.commonHelper.transcriptInternalRole(deselectedUser.type)
        })
        return;
    }
    this.OnItemDeSelect(user);
  }

  OnItemDeSelect(user: DropDownList) {
    if (user.id && user.itemName) {
        const listWithoutDeSelectedApprover = this.refund.approvers.filter(approver => approver._id !== user.id);
        this.refund.approvers = [...listWithoutDeSelectedApprover];
    }
  }

  onPaidCustomerChange() {
    if (!this.refund.paidCustomer) this.refund.valuePaidCustomer = undefined
  }

  validateSaveButton(): boolean {
    if((this.refund.paidCustomer && this.valuePaidInvalid()) || !this.selectedUsers.length) {
        return true
    }
    return false;
  }

  async downloadAllAttachments() {
    const attachments = this.refund.expenses
      .filter(e => e.attachment)
      .map(e => ({ name: e.attachment }));

    if (!attachments.length) {
      return this.toastr.warning("Este reembolso não possui anexos");
    }

    const signedUrlsPromises = attachments.map(attachment =>
      this.fileManager.getAccessUrl(attachment.name).then(url => ({
        name: attachment.name,
        url: url.accessUrl
      }))
    );

    const attachmentsWithUrls: any = await Promise.all(signedUrlsPromises);

    await this.jSZipProvider.newDownloadAllAttachments(attachmentsWithUrls, `Anexos_reembolso_${this.refund._id}`);
}



  cropExpenseDescription(description: string): string {
    if (!description || description == '') return '-'

    if (String(description).length > 50)
        return `${String(description).substring(0, 50)}...`

    return description
  }

  checkDescriptionLength(description: string | undefined): boolean {
    return description && description.length > 50;
  }

  expandDescription(expense): void {
    if (!this.checkDescriptionLength(expense.description))
        return;
    this.descriptionsDict[expense._id].active = !this.descriptionsDict[expense._id].active;
  }

  openAttachment(expense: any): void {
    if (!expense.attachment) return;
    this.fileManager.getAccessUrl(expense.attachment).then((res) => {
        window.open(res.accessUrl, '_blank')
    })
  }
}



// WEBPACK FOOTER //
// ./src/app/views/refunds-report/refund-detail/refund-detail.component.ts